import * as React from "react";
import { format } from "date-fns";
import {
  buildRedactedPseudoId,
  isFieldRedacted,
} from "@/lib/redacted-display";
import { getAge, parseBirthday } from "@/lib/utils";

interface UserInfoHeaderProps {
  name: string | null;
  pronouns?: string | null;
  birthday?: Date | null;
  reMatched?: boolean;
  sex?: string | null;
  redactedFields?: string[];
  profileRedactedFields?: string[];
  userId?: string;
}

export const UserInfoHeader = React.forwardRef<
  HTMLDivElement,
  UserInfoHeaderProps
>(
  (
    {
      name,
      pronouns,
      birthday,
      reMatched = false,
      sex,
      redactedFields,
      profileRedactedFields,
      userId,
    },
    ref,
  ) => {
    const nameRedacted = isFieldRedacted("name", redactedFields, name);
    const pronounsRedacted = isFieldRedacted(
      "pronouns",
      redactedFields,
      pronouns,
    );
    const birthdayRedacted = isFieldRedacted(
      "birthday",
      redactedFields,
      birthday,
    );
    const sexRedacted = isFieldRedacted("sex", profileRedactedFields, sex);

    const displayName = nameRedacted
      ? buildRedactedPseudoId(userId)
      : (name ?? "");

    return (
      <div ref={ref}>
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold font-avantt text-[20px]">
              {displayName}
            </h2>
            {reMatched && (
              <span className="rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-500">
                Re-matched
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-500 font-twk font-[14px] font-weight-400">
            {birthdayRedacted
              ? "[redacted]"
              : birthday
                ? `${format(parseBirthday(birthday), "MM/dd/yyyy")} (${getAge(birthday)} y/o)`
                : "?"}
          </span>
          {!pronounsRedacted && pronouns && (
            <>
              <span className="text-gray-500 font-twk font-[14px] font-weight-400">
                •
              </span>
              <span className="text-gray-500 font-twk font-[14px] font-weight-400">
                {pronouns}
              </span>
            </>
          )}
          {!sexRedacted && sex && (
            <>
              <span className="text-gray-500 font-twk font-[14px] font-weight-400">
                •
              </span>
              <span className="text-gray-500 font-twk font-[14px] font-weight-400">
                {sex}
              </span>
            </>
          )}
        </div>
      </div>
    );
  },
);

UserInfoHeader.displayName = "UserInfoHeader";
